/*
 * An address is always saved in parts, like a client's (Street 1, Street 2, City, State, ZIP), never as one free text
 * line (owner rule, 2026-11-02): mail, maps, ZIP routing and tax all need the parts. oneLine joins them where people read
 * them.
 */
import { z } from "zod";
import { stateCode } from "./client-import.js";

export const addressParts = z.object({
  street1: z.string().trim().min(1, "Type the street.").max(200),
  street2: z.string().trim().max(100).optional(),
  city: z.string().trim().min(1, "Type the city.").max(100),
  state: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/, "Use the 2 letter state, like LA.")
    // toUpperCase, not transform: a transform cannot go in the OpenAPI document, so /v1/openapi.json failed.
    .toUpperCase(),
  zip: z
    .string()
    .trim()
    .regex(/^\d{5}$/, "Use the 5 digit ZIP."),
});
export type AddressParts = z.infer<typeof addressParts>;

/** "1740 Shirley Dr, Suite 2, New Orleans, LA 70114". Parts left out are skipped; nothing at all gives "". */
export function oneLine(a: {
  street1?: string | null | undefined;
  street2?: string | null | undefined;
  city?: string | null | undefined;
  state?: string | null | undefined;
  zip?: string | null | undefined;
}): string {
  const place = [a.state, a.zip].filter(Boolean).join(" ");
  return [a.street1, a.street2, a.city, place]
    .map((p) => p?.trim())
    .filter(Boolean)
    .join(", ");
}

/**
 * A saved street with its city, state and ZIP, for maps and the pages crews read. Properties keep the street alone in
 * address, so Directions sent "1901 Manhattan Blvd" and the maps app could not find it (cleaner, 2026-11-02). An old
 * street that already holds its ZIP stays as it is.
 */
export function fullAddress(
  street: string,
  p: { city?: string | null | undefined; state?: string | null | undefined; zip?: string | null | undefined },
): string {
  if (p.zip && street.includes(p.zip)) return street;
  return oneLine({ street1: street, city: p.city, state: p.state, zip: p.zip });
}

export interface SplitAddress {
  street1: string | null;
  street2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

/**
 * A one line address from a website form ("244 Olivier Street, New Orleans, Louisiana, 70114") in parts: the ZIP is the
 * last 5 digits, the state a 2 letter code or state name, the city the piece before it, Street 2 anything between. When
 * no state or city is found the whole line stays Street 1, so nothing is lost.
 */
export function splitAddress(line: string | null | undefined, zip?: string | null): SplitAddress {
  const none: SplitAddress = { street1: null, street2: null, city: null, state: null, zip: null };
  const country = /[\s,]*\b(USA|US|United States)\.?$/i;
  let s = (line ?? "").replace(/\s+/g, " ").trim().replace(country, "");
  const zipOf = (v: string) => /\b(\d{5})(?:-\d{4})?\b/.exec(v)?.[1] ?? null;
  let z = zipOf(zip ?? "");
  // A ZIP at the end ("..., Louisiana, 70114", "..., LA 70114") comes off the line.
  const end = /[\s,]*\b(\d{5})(?:-\d{4})?\s*$/.exec(s);
  if (end) {
    z ??= end[1]!;
    s = s.slice(0, end.index);
  }
  s = s.replace(country, "");
  if (!s) return { ...none, zip: z };
  const parts = s
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  let state: string | null = null;
  const last = parts.at(-1) ?? "";
  if (parts.length > 1 && stateCode(last)) {
    state = stateCode(last);
    parts.pop();
  } else {
    // "New Orleans LA": the state rides on the city's piece.
    const m = /^(.+?)\s+([A-Za-z]{2})\.?$/.exec(last);
    if (parts.length > 1 && m && stateCode(m[2]!)) {
      state = stateCode(m[2]!);
      parts[parts.length - 1] = m[1]!;
    }
  }
  if (!state || parts.length < 2) return { ...none, street1: s, zip: z };
  const city = parts.pop()!;
  return { street1: parts[0]!, street2: parts.slice(1).join(", ") || null, city, state, zip: z };
}

/** A one line address as the columns a lead or quote keeps: Street 1 in address, then address2, city, state, zip. */
export function addressColumns(
  line: string | null | undefined,
  zip?: string | null,
): { address: string | null; address2: string | null; city: string | null; state: string | null; zip: string | null } {
  const p = splitAddress(line, zip);
  return { address: p.street1, address2: p.street2, city: p.city, state: p.state, zip: p.zip };
}
